import React, { useState, useEffect } from 'react';
import { X } from '@phosphor-icons/react';
import { useAnalyzerReportsContext } from '@Context/AnalyzerReportsContext';
import { AnalyzerWeeklyPrompt } from '@Models/types';

interface EditPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  prompt: AnalyzerWeeklyPrompt | null;
}

export const EditPromptModal: React.FC<EditPromptModalProps> = ({
  isOpen,
  onClose,
  prompt,
}) => {
  const [promptTitle, setPromptTitle] = useState('');
  const [promptString, setPromptString] = useState('');
  const [loading, setLoading] = useState(false);

  const { updatePrompt } = useAnalyzerReportsContext();

  useEffect(() => {
    if (prompt) {
      setPromptTitle(prompt.promptTitle);
      setPromptString(prompt.promptString);
    }
  }, [prompt]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt || !promptTitle.trim() || !promptString.trim()) return;

    setLoading(true);
    try {
      await updatePrompt(prompt.promptTitle, prompt.authorEmail, {
        promptTitle: promptTitle.trim(),
        promptString: promptString.trim(),
      });
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPromptTitle('');
    setPromptString('');
    onClose();
  };

  if (!isOpen || !prompt) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Edit Prompt</h2>
          <button
            onClick={handleClose}
            className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="promptTitle"
              className="block text-sm font-medium text-gray-700"
            >
              Prompt Title
            </label>
            <input
              id="promptTitle"
              type="text"
              value={promptTitle}
              onChange={(e) => setPromptTitle(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              placeholder="Enter prompt title"
              required
              disabled={prompt.isDefault}
            />
            {prompt.isDefault && (
              <p className="mt-1 text-sm text-gray-500">
                Default prompt title cannot be changed
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="promptString"
              className="block text-sm font-medium text-gray-700"
            >
              Prompt Content
            </label>
            <textarea
              id="promptString"
              value={promptString}
              onChange={(e) => setPromptString(e.target.value)}
              rows={12}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              placeholder="Enter prompt content"
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !promptTitle.trim() || !promptString.trim()}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Update Prompt'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
